import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Copy, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { formatDateLong } from '@/lib/format';
import { CONTACT } from '@/lib/contact';

// VerifyDrawPage
// A public verification tool. Fetches one past-draw record from the backend
// and lets any visitor: (a) see the seed, algorithm and full eligible ticket
// list; (b) recompute the pick locally in their browser using a
// JavaScript port of Python\'s random.Random (Mersenne Twister); and (c)
// confirm the result matches the published winning ticket.
//
// The JS Mersenne Twister below is a byte-compatible port of CPython\'s
// random module (`random.Random(seed).choice(sorted(list))`). It only
// implements what we need to reproduce the draw, so keep it minimal.

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// ---- Python-compatible Mersenne Twister --------------------------------- //
// Reference: CPython Lib/random.py + _randommodule.c. This is a well-known
// port used by several audit-trail tools. If Python changes its algorithm
// (extremely unlikely) we would update this + document the shift on this page.
function pyRandom(seed) {
  const N = 624;
  const M = 397;
  const MATRIX_A = 0x9908b0df >>> 0;
  const UPPER_MASK = 0x80000000 >>> 0;
  const LOWER_MASK = 0x7fffffff >>> 0;

  const mt = new Uint32Array(N);
  let index = N + 1;

  const initByArray = (initKey) => {
    // init_by_array — matches CPython\'s init from an arbitrary-length key.
    // Step 1: seed with 19650218.
    mt[0] = 19650218 >>> 0;
    for (let i = 1; i < N; i++) {
      mt[i] = (Math.imul(1812433253, mt[i - 1] ^ (mt[i - 1] >>> 30)) + i) >>> 0;
    }
    let i = 1;
    let j = 0;
    let k = Math.max(N, initKey.length);
    for (; k; k--) {
      const x = (mt[i - 1] ^ (mt[i - 1] >>> 30)) >>> 0;
      // 32-bit imul of x * 1664525
      const mul = Math.imul(x, 1664525) >>> 0;
      mt[i] = ((mt[i] ^ mul) + initKey[j] + j) >>> 0;
      i++; j++;
      if (i >= N) { mt[0] = mt[N - 1]; i = 1; }
      if (j >= initKey.length) j = 0;
    }
    for (k = N - 1; k; k--) {
      const x = (mt[i - 1] ^ (mt[i - 1] >>> 30)) >>> 0;
      const mul = Math.imul(x, 1566083941) >>> 0;
      mt[i] = ((mt[i] ^ mul) - i) >>> 0;
      i++;
      if (i >= N) { mt[0] = mt[N - 1]; i = 1; }
    }
    mt[0] = 0x80000000 >>> 0;
    index = N;
  };

  // Convert bigint seed to CPython\'s key array (little-endian 32-bit chunks).
  // Python\'s random.Random(seed) does `n = abs(seed); ... init_by_array(key)`
  // where key is n split into 32-bit little-endian words.
  const toKeyArray = (bigSeed) => {
    let n = bigSeed < 0n ? -bigSeed : bigSeed;
    if (n === 0n) return [0];
    const key = [];
    const mask = (1n << 32n) - 1n;
    while (n > 0n) {
      key.push(Number(n & mask));
      n = n >> 32n;
    }
    return key;
  };

  initByArray(toKeyArray(BigInt(seed)));

  const genrandUint32 = () => {
    if (index >= N) {
      for (let i = 0; i < N; i++) {
        const y = ((mt[i] & UPPER_MASK) | (mt[(i + 1) % N] & LOWER_MASK)) >>> 0;
        let next = (mt[(i + M) % N] ^ (y >>> 1)) >>> 0;
        if (y & 1) next = (next ^ MATRIX_A) >>> 0;
        mt[i] = next;
      }
      index = 0;
    }
    let y = mt[index++];
    y ^= (y >>> 11);
    y = (y ^ ((y << 7) & 0x9d2c5680)) >>> 0;
    y = (y ^ ((y << 15) & 0xefc60000)) >>> 0;
    y ^= (y >>> 18);
    return y >>> 0;
  };

  // random() — float in [0,1), matches CPython.
  const random = () => {
    const a = genrandUint32() >>> 5; // 27 bits
    const b = genrandUint32() >>> 6; // 26 bits
    return (a * 67108864 + b) * (1.0 / 9007199254740992.0);
  };

  // _randbelow_with_getrandbits(n) — mirrors CPython.
  const getrandbits = (k) => {
    // For k <= 32 we can just take one uint32.
    if (k <= 0) return 0n;
    if (k <= 32) return BigInt(genrandUint32() >>> (32 - k));
    // General case: assemble from 32-bit words, LOW to HIGH, and mask the top.
    let result = 0n;
    const words = Math.ceil(k / 32);
    for (let i = 0; i < words; i++) {
      result |= BigInt(genrandUint32()) << BigInt(32 * i);
    }
    const excess = 32 * words - k;
    if (excess > 0) result &= (1n << BigInt(k)) - 1n;
    return result;
  };

  const randbelowN = (n) => {
    if (n <= 0) throw new Error('n must be positive');
    const nBig = BigInt(n);
    const k = nBig.toString(2).length; // bit_length
    let r = getrandbits(k);
    while (r >= nBig) r = getrandbits(k);
    return Number(r);
  };

  const choice = (seq) => {
    if (seq.length === 0) throw new Error('empty sequence');
    return seq[randbelowN(seq.length)];
  };

  return { random, getrandbits, choice };
}
// ------------------------------------------------------------------------ //

const VerifyDrawPage = () => {
  const { id } = useParams();
  const [draw, setDraw] = useState(null);
  const [err, setErr] = useState(null);
  const [computed, setComputed] = useState(null);
  const [computing, setComputing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/past-draws/${id}`)
      .then((r) => setDraw(r.data))
      .catch(() => setErr('Draw not found or has not been published yet.'));
  }, [id]);

  const runVerification = () => {
    if (!draw) return;
    setComputing(true);
    setTimeout(() => {
      try {
        const rng = pyRandom(draw.seed);
        const sortedList = [...(draw.eligible_ticket_numbers || [])].sort((a, b) => a - b);
        const picked = rng.choice(sortedList);
        setComputed(picked);
      } catch (e) {
        setErr(`Verification failed to run: ${e.message}`);
      } finally {
        setComputing(false);
      }
    }, 50);
  };

  const match = useMemo(() => computed !== null && draw && computed === draw.winner_ticket_number, [computed, draw]);

  const copy = (v) => navigator.clipboard?.writeText(String(v));

  if (err) return (
    <div className="container-pd py-16 max-w-3xl">
      <Link to="/past-draws" className="text-sm text-muted-sage inline-flex items-center gap-1"><ArrowLeft size={14} /> Back to past draws</Link>
      <h1 className="font-display text-4xl mt-4">Draw not found.</h1>
      <p className="mt-3 text-muted-sage">{err}</p>
    </div>
  );

  if (!draw) return (
    <div className="container-pd py-16 max-w-3xl">
      <div className="text-sm text-muted-sage">Loading draw record…</div>
    </div>
  );

  const pythonSnippet = `import random

seed = ${draw.seed}
eligible = ${JSON.stringify(draw.eligible_ticket_numbers || [])}
winner = random.Random(seed).choice(sorted(eligible))
print(winner)  # expect: ${draw.winner_ticket_number}`;

  return (
    <div className="container-pd py-14 md:py-20 max-w-4xl" data-testid="verify-draw-page">
      <Link to="/past-draws" className="text-sm text-muted-sage inline-flex items-center gap-1 hover:text-[color:var(--pd-text)]">
        <ArrowLeft size={14} /> Back to past draws
      </Link>
      <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-[0.2em] mt-6">Independent verification</div>
      <h1 className="font-display text-4xl md:text-5xl leading-none mt-3">Verify this draw.</h1>
      <p className="mt-3 text-lg text-muted-sage max-w-3xl">
        Recompute the winning ticket for <span className="text-[color:var(--pd-text)]">{draw.competition_title}</span> yourself — either in your own Python interpreter (snippet below), or with the built-in verifier that runs entirely in your browser (button below). Both use the same algorithm as our backend.
      </p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div className="bg-surface border border-pd rounded-xl p-4">
          <div className="font-mono text-[10px] text-muted-sage uppercase">Drawn at</div>
          <div className="mt-1 text-[color:var(--pd-text)]">{formatDateLong(draw.drawn_at)}</div>
        </div>
        <div className="bg-surface border border-pd rounded-xl p-4">
          <div className="font-mono text-[10px] text-muted-sage uppercase">Eligible tickets</div>
          <div className="mt-1 font-mono text-[color:var(--pd-text)]">{(draw.eligible_count || 0).toLocaleString('en-GB')}</div>
        </div>
        <div className="bg-surface border border-pd rounded-xl p-4">
          <div className="font-mono text-[10px] text-muted-sage uppercase">Independent witness</div>
          <div className="mt-1 text-[color:var(--pd-text)]">
            {draw.witnessed ? (
              draw.witness_name ? (
                <span>{draw.witness_name}{draw.witness_role ? <span className="text-muted-sage">, {draw.witness_role}</span> : null}</span>
              ) : (
                <span>Independently witnessed</span>
              )
            ) : (
              <span className="text-[color:var(--pd-danger)]">No witness recorded</span>
            )}
          </div>
          {draw.witnessed && !draw.witness_name && (
            <div
              className="mt-2 text-[11px] leading-relaxed text-muted-sage"
              data-testid="witness-anonymous-note"
            >
              The observer&rsquo;s full name and role are kept on the internal audit trail and can
              be produced on request to a regulator or formal complainant.
            </div>
          )}
        </div>
      </div>

      {/* Seed + algorithm */}
      <div className="mt-6 rounded-2xl border border-pd bg-surface p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="font-mono text-[10px] text-muted-sage uppercase">Seed (64-bit integer)</div>
          <button onClick={() => copy(draw.seed)} className="text-xs text-muted-sage hover:text-[color:var(--pd-accent-yellow)] inline-flex items-center gap-1"><Copy size={12} /> Copy</button>
        </div>
        <div className="mt-1 font-mono text-sm break-all text-[color:var(--pd-text)]" data-testid="verify-seed">{String(draw.seed)}</div>
        <div className="mt-4 font-mono text-[10px] text-muted-sage uppercase">Algorithm</div>
        <div className="mt-1 font-mono text-sm break-all text-[color:var(--pd-text)]">{draw.algorithm}</div>
      </div>

      {/* Eligible ticket list */}
      <div className="mt-4 rounded-2xl border border-pd bg-surface p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="font-mono text-[10px] text-muted-sage uppercase">Eligible ticket numbers (sorted)</div>
          <button onClick={() => copy(JSON.stringify(draw.eligible_ticket_numbers || []))} className="text-xs text-muted-sage hover:text-[color:var(--pd-accent-yellow)] inline-flex items-center gap-1"><Copy size={12} /> Copy list</button>
        </div>
        <pre className="mt-2 font-mono text-xs bg-[color:var(--pd-bg)] border border-pd rounded-lg p-3 overflow-auto max-h-48 text-[color:var(--pd-text)]" data-testid="verify-eligible">{JSON.stringify(draw.eligible_ticket_numbers || [])}</pre>
      </div>

      {/* Python snippet */}
      <div className="mt-4 rounded-2xl border border-pd bg-surface p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="font-mono text-[10px] text-muted-sage uppercase">Copy-paste into any Python 3 interpreter</div>
          <button onClick={() => copy(pythonSnippet)} className="text-xs text-muted-sage hover:text-[color:var(--pd-accent-yellow)] inline-flex items-center gap-1"><Copy size={12} /> Copy snippet</button>
        </div>
        <pre className="mt-2 font-mono text-xs bg-[color:var(--pd-bg)] border border-pd rounded-lg p-3 overflow-auto text-[color:var(--pd-text)]">{pythonSnippet}</pre>
      </div>

      {/* In-browser verifier */}
      <div className="mt-4 rounded-2xl border border-[color:var(--pd-gold)]/50 bg-surface p-5">
        <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-widest">Run it here</div>
        <p className="mt-2 text-sm text-muted-sage">
          Click the button below to run the same algorithm in your browser using a JavaScript port of Python&rsquo;s <code className="font-mono text-[color:var(--pd-text)]">random.Random</code>. Nothing is sent to us — the computation happens entirely on your device.
        </p>
        <button
          onClick={runVerification}
          disabled={computing}
          className="mt-4 inline-flex items-center gap-2 bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-xl px-4 py-2.5 font-medium disabled:opacity-60"
          data-testid="verify-run-button"
        >
          {computing ? 'Computing…' : 'Recompute the winning ticket'}
        </button>

        {computed !== null && (
          <div className="mt-5 rounded-xl border border-pd bg-[color:var(--pd-bg)] p-4" data-testid="verify-result">
            <div className="font-mono text-[10px] text-muted-sage uppercase">Your computation</div>
            <div className="mt-1 flex items-baseline gap-4">
              <div className="font-display text-3xl text-[color:var(--pd-text)]">Ticket {computed}</div>
              <div className="text-sm text-muted-sage">vs. published winner: <span className="font-mono text-[color:var(--pd-text)]">{draw.winner_ticket_number}</span></div>
            </div>
            <div className="mt-3">
              {match ? (
                <span className="inline-flex items-center gap-2 text-[color:var(--pd-text)]" data-testid="verify-match">
                  <CheckCircle2 size={18} className="text-[color:var(--pd-gold)]" />
                  <span>Match confirmed. The published winner is exactly what the seeded RNG produces.</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 text-[color:var(--pd-danger)]" data-testid="verify-mismatch">
                  <XCircle size={18} />
                  <span>Mismatch. Please email <a className="underline" href={CONTACT.emailHref}>{CONTACT.email}</a> — this is something we&rsquo;d want to investigate immediately.</span>
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <p className="mt-10 text-xs text-muted-sage">
        This verifier is provided as a convenience. The authoritative record is the immutable draw entry on our database, published at{' '}
        <Link to="/past-draws" className="text-[color:var(--pd-accent-yellow)]">/past-draws</Link>. Any question about this draw — call us on{' '}
        <a href={CONTACT.phoneHref} className="text-[color:var(--pd-accent-yellow)]">{CONTACT.phoneDisplay}</a> or email{' '}
        <a href={CONTACT.emailHref} className="text-[color:var(--pd-accent-yellow)]">{CONTACT.email}</a>.
      </p>
    </div>
  );
};

export default VerifyDrawPage;
